'use client';

import { ResponsiveOverlay } from '@components/mobile/overlays/responsive-overlay';
import { Button } from '@components/ui/button';
import AgentPicker from '@components/people/AgentPicker';
import { useQuery } from '@rocicorp/zero/react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { FC, useState } from 'react';
import { startAgentDirectMessage } from '../../app/(application)/[organization_id]/messages/actions';
import { queries } from '../../zero/queries';
import { useCurrentOrgUser } from '../../zero/use-current-user';
import { getHref, ROUTE } from '@utils/route-utils';
import { channelLabel } from '../messages/channel-label';
import { setPendingDraft } from '../messages/pending-draft';
import { toast } from 'sonner';

export type AskAboutThisSource = { kind: string; id: string; title: string };

type AskAboutThisDialogProps = {
  organizationId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  question: string;
  sources: AskAboutThisSource[];
};

function composeDraft(question: string, sources: AskAboutThisSource[]): string {
  if (!sources.length) return question;
  const list = sources.map(source => `- ${source.title}`).join('\n');
  return `${question}\n\nSources:\n${list}`;
}

/**
 * "Ask about this" (Phase G item 4): reuses the human-agent DM creation
 * path (item 1) or an existing conversation that already has an agent.
 * Only the question text and source titles travel with it -- never a full
 * search excerpt -- and the draft lands in the composer for the user to
 * review/edit before sending, so the server's own audience checks in
 * `retrieveKnowledgeForAudience` still apply at send time.
 */
const AskAboutThisDialog: FC<AskAboutThisDialogProps> = ({
  organizationId,
  open,
  onOpenChange,
  question,
  sources
}) => {
  const t = useTranslations('knowledge-search');
  const router = useRouter();
  const orgUser = useCurrentOrgUser(organizationId);
  const [organization] = useQuery(
    open ? queries.organizations.withChannels({ organizationId }) : undefined
  );
  const [agentId, setAgentId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  const me = orgUser?.userId;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const channels = ((organization as any)?.chatChannels ?? []) as any[];
  const existingWithAgent = channels.filter(
    channel =>
      channel.members?.some((member: any) => member.userId === me) &&
      channel.members?.some((member: any) => member.agentEnabled)
  );

  const goToChannel = (channelId: string) => {
    setPendingDraft(channelId, composeDraft(question, sources));
    onOpenChange(false);
    router.push(getHref(organizationId, [ROUTE.MESSAGES, channelId]));
  };

  const startNew = async () => {
    if (!agentId || starting) return;
    setStarting(true);
    try {
      const { channelId } = await startAgentDirectMessage({
        organizationId,
        agentUserIds: [agentId]
      });
      goToChannel(channelId);
    } catch {
      toast.error(t('askAgentError'));
    } finally {
      setStarting(false);
    }
  };

  return (
    <ResponsiveOverlay
      open={open}
      onOpenChange={onOpenChange}
      title={t('askAgentTitle')}
      description={t('askAgentDescription')}
    >
      <div className="flex flex-col gap-4 py-2">
        <div className="grid gap-2">
          <p className="text-label text-foreground">
            {t('askAgentNewSection')}
          </p>
          <AgentPicker
            organizationId={organizationId}
            value={agentId ? [agentId] : []}
            onChange={next => setAgentId(next.at(-1) ?? null)}
            maxSelections={1}
          />
          <Button
            type="button"
            variant="primary"
            className="self-start"
            disabled={!agentId || starting}
            onClick={() => void startNew()}
          >
            {t('askAgentStart')}
          </Button>
        </div>
        {existingWithAgent.length > 0 ? (
          <div className="grid gap-2">
            <p className="text-label text-foreground">
              {t('askAgentExistingSection')}
            </p>
            <ul className="flex flex-col gap-1">
              {existingWithAgent.map(channel => (
                <li key={channel.id}>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => goToChannel(channel.id)}
                  >
                    {channelLabel(channel, me) || channel.name}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </ResponsiveOverlay>
  );
};

export default AskAboutThisDialog;
